import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  App as AntApp,
  Button,
  Empty,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { errorMessage } from '../../api';
import type { BillingApi, EmailAnchor, EmailRule, EmailTemplate } from '../../billingApi';

const { Text } = Typography;

const ANCHORS: Array<{ value: EmailAnchor; label: string }> = [
  { value: 'signup', label: 'Sign-up' },
  { value: 'trial_end', label: 'Trial end' },
  { value: 'access_ended', label: 'Access ended' },
];

/** "3 days before trial end" in words. */
export function ruleWhen(rule: Pick<EmailRule, 'anchor' | 'offset_days'>): string {
  const anchor = ANCHORS.find((a) => a.value === rule.anchor)?.label.toLowerCase() ?? rule.anchor;
  const n = Math.abs(rule.offset_days);
  if (n === 0) return `on ${anchor}`;
  return `${n} day${n === 1 ? '' : 's'} ${rule.offset_days < 0 ? 'before' : 'after'} ${anchor}`;
}

/** The days field saves when you leave it or press Enter, not on every keystroke. */
const DaysInput: React.FC<{ value: number; onSave: (days: number) => void }> = ({
  value,
  onSave,
}) => {
  const [draft, setDraft] = useState<number | null>(value);
  const commit = () => {
    if (draft === null) setDraft(value);
    else if (draft !== value) onSave(draft);
  };
  return (
    <Space size={8}>
      <InputNumber
        aria-label="Days"
        min={-365}
        max={730}
        precision={0}
        value={draft}
        onChange={setDraft}
        onBlur={commit}
        onPressEnter={commit}
        style={{ width: 90 }}
      />
      <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
        {draft === null ? '' : draft < 0 ? 'before' : 'after'}
      </Text>
    </Space>
  );
};

/** Which email goes out when: every rule is a row you can change in place. */
export const RulesTab: React.FC<{ billing: BillingApi }> = ({ billing }) => {
  const { message } = AntApp.useApp();
  const [rules, setRules] = useState<EmailRule[] | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [adding, setAdding] = useState<{
    template_key: string | null;
    anchor: EmailAnchor;
    days: number | null;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([billing.listRules(), billing.listTemplates()])
      .then(([r, t]) => {
        if (cancelled) return;
        setRules(r);
        setTemplates(t);
        setLoadError(null);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, [billing, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const update = async (rule: EmailRule, fields: Parameters<BillingApi['updateRule']>[1]) => {
    try {
      const saved = await billing.updateRule(rule.id, fields);
      setRules((list) => list?.map((r) => (r.id === saved.id ? saved : r)) ?? null);
    } catch (e) {
      message.error(errorMessage(e));
      reload();
    }
  };

  const remove = async (rule: EmailRule) => {
    try {
      await billing.deleteRule(rule.id);
      setRules((list) => list?.filter((r) => r.id !== rule.id) ?? null);
    } catch (e) {
      message.error(errorMessage(e));
      reload();
    }
  };

  const create = async () => {
    if (!adding?.template_key || adding.days === null) return;
    setSaving(true);
    try {
      await billing.createRule({
        template_key: adding.template_key,
        anchor: adding.anchor,
        offset_days: adding.days,
      });
      setAdding(null);
      reload();
    } catch (e) {
      message.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        title="Could not load the rules"
        description={loadError}
        action={<Button onClick={reload}>Try again</Button>}
      />
    );
  }
  if (!rules) return <Spin style={{ display: 'block', marginTop: 32 }} />;

  const templateOptions = templates.map((t) => ({ value: t.key, label: t.key }));

  const columns = [
    {
      title: 'Email',
      key: 'template',
      render: (_: unknown, r: EmailRule) => (
        <Select
          aria-label="Email"
          value={r.template_key}
          options={templateOptions}
          style={{ minWidth: 200 }}
          onChange={(v) => void update(r, { template_key: v })}
        />
      ),
    },
    {
      title: 'Counted from',
      key: 'anchor',
      render: (_: unknown, r: EmailRule) => (
        <Select
          aria-label="Counted from"
          value={r.anchor}
          options={ANCHORS}
          style={{ width: 150 }}
          onChange={(v) => void update(r, { anchor: v })}
        />
      ),
    },
    {
      title: 'Days',
      key: 'days',
      render: (_: unknown, r: EmailRule) => (
        <DaysInput
          key={r.offset_days}
          value={r.offset_days}
          onSave={(d) => void update(r, { offset_days: d })}
        />
      ),
    },
    {
      title: 'Only if not subscribed',
      key: 'only',
      width: 190,
      render: (_: unknown, r: EmailRule) => (
        <Switch
          aria-label="Only if not subscribed"
          checked={r.only_unsubscribed}
          onChange={(v) => void update(r, { only_unsubscribed: v })}
        />
      ),
    },
    {
      title: 'On',
      key: 'active',
      width: 80,
      render: (_: unknown, r: EmailRule) => (
        <Switch
          aria-label="Active"
          checked={r.active}
          onChange={(v) => void update(r, { active: v })}
        />
      ),
    },
    {
      title: '',
      key: 'delete',
      width: 60,
      render: (_: unknown, r: EmailRule) => (
        <Popconfirm
          title={`Delete this rule (${ruleWhen(r)})?`}
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => void remove(r)}
        >
          <Button type="text" danger icon={<DeleteOutlined />} aria-label="Delete rule" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Button
        icon={<PlusOutlined />}
        style={{ marginBottom: 16 }}
        onClick={() => setAdding({ template_key: null, anchor: 'trial_end', days: -3 })}
      >
        Add rule
      </Button>

      <Table<EmailRule>
        rowKey="id"
        size="middle"
        dataSource={rules}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: <Empty description="No rules yet." /> }}
      />

      <Modal
        open={!!adding}
        title="Add rule"
        okText="Add"
        confirmLoading={saving}
        okButtonProps={{ disabled: !adding?.template_key || adding.days === null }}
        onCancel={() => setAdding(null)}
        onOk={() => void create()}
        destroyOnHidden
      >
        {adding && (
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Select
              aria-label="Email"
              placeholder="Email"
              value={adding.template_key}
              options={templateOptions}
              style={{ width: '100%' }}
              onChange={(v) => setAdding({ ...adding, template_key: v })}
            />
            <Select
              aria-label="Counted from"
              value={adding.anchor}
              options={ANCHORS}
              style={{ width: '100%' }}
              onChange={(v) => setAdding({ ...adding, anchor: v })}
            />
            <Space>
              <InputNumber
                aria-label="Days"
                min={-365}
                max={730}
                precision={0}
                value={adding.days}
                onChange={(v) => setAdding({ ...adding, days: v })}
                style={{ width: 100 }}
              />
              <Text type="secondary">
                {adding.days === null
                  ? ''
                  : ruleWhen({ anchor: adding.anchor, offset_days: adding.days })}
              </Text>
            </Space>
          </Space>
        )}
      </Modal>
    </div>
  );
};
